// COPPA COMPLIANT: No data stored, no API calls, local state only
// Pre-programmed bot for children under 13 — NO OpenAI calls, NO Firestore storage.
// All responses are pre-canned. No child data leaves the device.

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  Chip,
  Collapse,
  Divider
} from '@mui/material';
import { Send, ExpandMore, ExpandLess } from '@mui/icons-material';

// ── Pre-programmed response bank ──────────────────────────────────────────────
const RESPONSES = {
  // ── Original 12 categories ─────────────────────────────────────────────────
  greeting: [
    "Hey there! I'm Chip! Ready to crush those chores today? 🌟",
    "Hi! Chip here! What are we tackling today? 🏆",
    "Hello! It's your buddy Chip! Let's make today awesome! ⭐",
    "Heyyy! Great to see you! Ready to earn some awesome rewards? 🎉",
    "What's up! Chip is HERE and ready to cheer you on! 💪",
    "Hi hi hi! You showed up today — that already makes you a winner! 🌈"
  ],
  motivation: [
    "You're doing amazing! Keep going! 💪",
    "Every chore you finish is a WIN! You've got this! 🎯",
    "Champions don't quit — and you're a champion! 🏆",
    "Hard work always pays off. You're proving it every day! 🌟",
    "Wow, you're on FIRE! Keep that energy going! 🔥",
    "Even when it's tough, you keep pushing — that's what legends do! ⚡",
    "One step at a time gets you all the way there. You're proof! 🚀",
    "Your future self is going to be SO proud of the work you're doing right now! 🌠"
  ],
  chores: [
    "Doing chores helps your whole family and earns you rewards! 🏠",
    "Every task you finish makes your home a happier place! ✨",
    "Chores are like leveling up — the more you do, the stronger you get! 💪",
    "Tip: Break big chores into small steps — it makes them way easier! 📋",
    "The fastest way to finish a chore? Just start! You can do it! 🚀",
    "Set a timer for 10 minutes and see how much you can knock out — it's a game! ⏱️",
    "Put on your favorite music while you work — chores fly by with a good beat! 🎵",
    "Messy to clean is like a puzzle — you get to figure it all out! 🧩"
  ],
  rewards: [
    "Every completed chore gets you closer to your goal! 🎯",
    "Your hard work turns into real rewards — keep it up! 💰",
    "The more chores you finish, the more you earn! You're building something great! 🌟",
    "Save up those rewards — you're going to love what you can get! 🎁",
    "Rewards are waiting for you — finish those chores! 🏆",
    "Every point you earn is YOU choosing your future — super cool! ✨",
    "Think about what you're saving for — let that goal push you forward! 🎮",
    "Work now, reward later — that's how champions operate! 🥇"
  ],
  boredom: [
    "Boredom is your brain saying it's ready for a challenge! 🧠",
    "Sounds like a great time to pick a chore and crush it! You'll feel way better after! 💪",
    "Bored? Check your task list — you might have an easy win waiting! 🏆",
    "Fun idea: race yourself! Pick a chore and beat the clock! ⏱️",
    "Boredom is secretly an invitation to try something new — what sounds interesting? 🌟",
    "Sometimes the antidote to boredom is just moving — pick something small and go! 🚀"
  ],
  difficulty: [
    "Hard things are just easy things you haven't practiced yet! You've got this! 💪",
    "Try breaking it into tiny steps — what's the very first small thing you can do? 🐜",
    "Ask a parent for help — working together makes hard things easier! 🤝",
    "Every expert was once a beginner. Keep trying! ⭐",
    "Struggling means you're growing — it's supposed to feel hard at first! 🌱",
    "Take a deep breath, reset, and try just the first tiny piece. You can do it! 🎯",
    "Hard tasks make the reward feel even BETTER when you finish! Push through! 🏆"
  ],
  feelings: [
    "It's okay to feel frustrated sometimes! Take a breath and come back to it. 🌬️",
    "Feeling overwhelmed? Try just ONE thing on your list. Just one! 🎯",
    "Your feelings are totally valid. If you're having a hard day, a short break is okay. 💙",
    "Even the toughest days end. Tomorrow is a brand new chance! 🌅",
    "It's okay to not be okay. Talk to a grown-up if things feel really heavy. 💙",
    "Taking care of your feelings IS a kind of superpower. Be kind to yourself! 🌸",
    "Everyone has rough days — what matters is that you keep showing up! 🌟"
  ],
  accomplishment: [
    "YOU DID IT! That is huge — seriously, be so proud! 🎉",
    "Look at you go! Nothing can stop you now! 🚀",
    "Champion move! Finishing tasks is literally a superpower! 🏆",
    "BOOM! Task crushed! You are unstoppable! 💥",
    "That's what I'm talking about! LEGEND STATUS achieved! 🌟",
    "High five through the screen! You earned this moment! ✋",
    "Every time you finish something, your future gets a little brighter! 🌈",
    "You said you'd do it, and you DID it. That's called being amazing! ⭐"
  ],
  money: [
    "Every task you finish is earning you real money! Keep it up! 💰",
    "Saving up for something? You're getting closer with every chore! 🎯",
    "Small amounts add up fast — even a little every day becomes something real! 🌱",
    "Learning to earn money now sets you up for an awesome future! 💡",
    "Spend wisely, save some — you're learning skills most adults wish they knew! 🏦",
    "Every coin earned by working feels so much better than one just handed to you! 💪",
    "You're not just earning money — you're learning responsibility. That's priceless! 🌟"
  ],
  school: [
    "School and chores together? That is a winning combination! 📚",
    "Homework done plus chores done equals unstoppable! 📖",
    "Smart and responsible — that is you! 🌟",
    "Finishing school stuff first makes everything else feel easier! 🎒",
    "Every lesson you learn at school is a superpower you get to keep forever! 🧠",
    "Chip tip: homework first, then chores, then PLAY — best day ever! 🎉",
    "You're building your brain AND your skills. Double champion! 🏆"
  ],
  help: [
    "I'm Chip, your chore helper! Ask me about tasks, rewards, or just say hi! 😊",
    "Need help? I'm here! I can cheer you on and give chore tips! 🎉",
    "You can ask me about chores, rewards, or just chat! I'm always here for you! 💬",
    "Chip here! Try asking about chores, how to earn rewards, or just tell me how your day is going! 🌟",
    "Not sure what to say? Try: 'I'm bored', 'motivate me', 'chore tips', or 'hi Chip'! 😄"
  ],
  default: [
    "That's awesome! Keep being amazing! ⭐",
    "You're doing great! I'm proud of you! 🌟",
    "Every day is a new chance to be a chore champion! 🏆",
    "Keep smiling and keep going — you've totally got this! 💪",
    "I believe in you! Now go show those chores who's boss! 🚀",
    "Remember: small steps lead to big wins! Keep it up! 🎯",
    "You are one-of-a-kind and totally capable of amazing things! 🌈",
    "Whatever you're up to today, give it your best — that's always enough! ✨"
  ],

  // ── New Categories (10) ───────────────────────────────────────────────────
  friends: [
    "Friends make everything more fun! Good friendships are one of the best things ever! 👫",
    "Being a good friend means listening, sharing, and showing up — sounds like you! 🌟",
    "If you had a fight with a friend, a calm conversation can fix most things! 💬",
    "True friends cheer each other on — sounds like something Chip would do! 🏆",
    "Sharing can be hard sometimes, but it always feels great after! ✨",
    "A good friend is someone who likes you just the way you are. You deserve that! 💙",
    "Making new friends can feel scary — but a simple 'hi' is all it takes to start! 👋",
    "When friends disagree, it's okay! What matters is working it out kindly. 🤝",
    "Being kind to someone you don't know yet might be the start of an awesome friendship! 🌈",
    "Your kindness is the best thing you can bring to any friendship! 💛"
  ],

  family: [
    "Family is your team — and teams work best when everyone helps out! 🏠",
    "Helping your family with chores is one of the coolest things you can do! 🌟",
    "Spending time with family, even doing simple stuff, makes great memories! 💛",
    "Siblings can be tricky sometimes — but they're also built-in best friends! 😄",
    "Doing something nice for your family today? That's a total champion move! 🏆",
    "Every family is different, and that's what makes yours special! 🌈",
    "Parents and guardians work so hard — helping out is a way to show you see that! 💙",
    "Even small things, like saying thank you, mean a LOT to your family! ✨",
    "Family game nights, cooking together, even just talking — that stuff is gold! 🥇",
    "The people who cheer for you most? Usually your family. They're on your side! 💪"
  ],

  pets: [
    "Pets count on US to take care of them — that's a big and awesome responsibility! 🐾",
    "Feeding, walking, and brushing your pet are real chores that really matter! 🐶",
    "Animals show love in their own way — taking care of them is how you show it back! 💛",
    "Pet care is actually one of the most important chores there is! They can't do it themselves! 🐱",
    "A walk with a dog, a chat with a hamster — pets make every day better! 🌟",
    "Taking care of a living thing teaches you patience, love, and responsibility! 🏆",
    "Your pet thinks you're the best human ever — don't let them down! 🐾",
    "Whether it's a fish, dog, cat, or hamster — they all need YOU to show up for them! ✨",
    "Pet chores are love chores — every scoop and every bowl of food is an act of kindness! 💙",
    "If you have a pet, make sure fresh water and food are always ready — that's hero work! 🦸"
  ],

  hobbies: [
    "Having a hobby you love makes life so much more fun! What are you into? 🎨",
    "Drawing, building, reading, music — creativity is one of your superpowers! 🌟",
    "Practicing something you love every day is how you get REALLY good at it! 💪",
    "Sports, art, reading — all amazing ways to grow your mind and body! 🏅",
    "The things you enjoy now might become something you're famous for someday! 🚀",
    "Building things with your hands is a skill that lasts your whole life! 🔧",
    "Reading is like a superpower — it makes your brain stronger AND takes you on adventures! 📚",
    "Music is math you can feel — playing an instrument is seriously impressive! 🎵",
    "Whatever you love to create, keep creating. The world needs YOUR ideas! 💡",
    "A hobby isn't just fun — it's YOU figuring out what you're amazing at! 🌈",
    "Every great artist, athlete, and inventor started exactly where you are. Keep going! ⭐"
  ],

  games: [
    "Games are so fun — and they actually teach strategy and thinking! 🎮",
    "Whether it's video games, board games, or outside games — play is important! 🌟",
    "Board games with family or friends are some of the best times ever! 🎲",
    "Outdoor games keep your body strong and make amazing memories! 🏃",
    "Winning feels great, but learning from losing is what makes you a real champion! 🏆",
    "Tip: finish your chores BEFORE game time and you can enjoy it guilt-free! ✅",
    "Teamwork in games is just like teamwork at home — everyone has a role! 🤝",
    "Playing games is a great way to relax after finishing your tasks! 🎉",
    "Card games, video games, tag — every game you play is building your brain! 🧠",
    "Chip's rule: chores first, games after = MAXIMUM fun with zero guilt! 🥇"
  ],

  tv_movies: [
    "TV time is a great reward after you've crushed your chores! 📺",
    "Watching something you love is a perfect way to wind down! 🌟",
    "Good shows and movies can teach you things AND entertain you — double win! 🎬",
    "Chip's tip: earn your screen time by checking off those tasks first! ✅",
    "Movies and shows spark your imagination — and imagination leads to big ideas! 💡",
    "Sharing a show you love with your family is a really fun experience! 🌈",
    "Whether it's cartoons, adventures, or nature shows — there's always something amazing to discover! 🦁",
    "After all those chores, you've totally earned some chill screen time! 🎉",
    "Watching stories helps us understand the world and other people — pretty powerful! 🌍",
    "Finish your responsibilities and then enjoy your favorites — that's the winning formula! 🏆"
  ],

  weather_seasons: [
    "Rain, sun, or snow — every kind of weather has something awesome about it! ⛅",
    "Rainy days are perfect for indoor chores — knock them out while it pours! 🌧️",
    "Snow days are chore days AND play days — best combo! ❄️",
    "Spring cleaning is a real thing — and it feels SO good when it's done! 🌸",
    "Summer means more free time — perfect for getting ahead on chores and earning more! ☀️",
    "Fall leaves mean raking — and raking means fresh air and rewards! 🍂",
    "Every season has its own chores and its own fun — what season do you like best? 🌟",
    "Cold outside? Perfect excuse to cozy up inside and get stuff done! 🏠",
    "Hot day? Chip says: indoor chores first, then water fun! 💦",
    "Seasonal chores like shoveling snow or raking leaves are real team contributions! 🏆"
  ],

  weekend_plans: [
    "Weekends are amazing! Finish your chores and the whole day is yours! 🌟",
    "Big plans this weekend? Getting chores done early means way more free time! ✅",
    "Vacations and trips are so fun — and you can earn rewards even then! 🗺️",
    "Weekend mornings are the BEST time to blitz through your tasks! ⚡",
    "Whether you're staying home or going out, chores still need doing — quick, quick, go! 🚀",
    "Plan for fun AND plan for responsibility — that's the weekend champion way! 🏆",
    "Even on weekends, a little work makes a big difference! Your family notices! 💛",
    "Adventures are more fun when your responsibilities are already handled! 🌈",
    "Chip's weekend tip: 30 minutes of chores = the rest of the day free! ⏱️",
    "Whatever you're doing this weekend, remember to have fun AND be helpful! 😄"
  ],

  food: [
    "Food is fuel for champions! Eating well helps you have energy for chores AND play! 🥗",
    "Helping cook or set the table is a real contribution to your family! 🍽️",
    "Trying new foods is like going on a taste adventure — be brave! 🌮",
    "Cleaning up after meals is one of the most helpful chores you can do! ✨",
    "Cooking together as a family is a fun way to spend time AND learn skills! 👨‍🍳",
    "Even little helpers in the kitchen are BIG helpers — every bit counts! 🌟",
    "Chip fun fact: the more you help in the kitchen, the more choices you might get! 😄",
    "Washing dishes, wiping counters, putting things away — kitchen chores are important! 🏆",
    "Healthy snacks keep your brain sharp and your energy up — choose good fuel! ⚡",
    "Helping prepare food for your family is an act of love AND a real life skill! 💛"
  ],

  dreams_goals: [
    "Dream BIG — there's no limit on what you can become! 🚀",
    "Every chore you finish is training your brain to achieve goals — seriously! 🧠",
    "Whatever you want to be someday, hard work and responsibility get you there! 🌟",
    "The habits you build NOW are the ones that will carry you to your dreams! 💪",
    "Scientists, artists, athletes, builders — they all started by showing up and doing the work! 🏆",
    "Your dreams are valid, and YOU have what it takes to chase them! ✨",
    "Setting a goal and working toward it — that's exactly what you're doing with your chores! 🎯",
    "Big goals are just a bunch of small daily steps. You're already on the path! 🌈",
    "Never stop imagining what you could be — AND keep doing your chores today! 😄",
    "The best version of you is being built one responsible choice at a time! 🌠",
    "Chip believes in your dreams 100% — and so should YOU! 💙"
  ]
};

let defaultIndex = 0;

function getResponse(input, tasks = [], completedTasks = []) {
  const msg = input.toLowerCase();

  // ── Crisis safety check — always first ──────────────────────────────────────
  const crisisKeywords = ['hurt myself', 'want to die', 'kill myself', 'nobody cares', 'hate myself so much', 'self harm', 'run away tonight'];
  if (crisisKeywords.some(kw => msg.includes(kw))) {
    return "I am really glad you are talking. If you are having a really tough time, please tell a grown-up you trust right away — a parent, teacher, or school counselor. You can also text HOME to 741741 for free help anytime. You matter a lot. 💙";
  }

  // ── Original 12 categories ─────────────────────────────────────────────────

  if (msg.match(/\b(hi|hello|hey|howdy|sup|yo|hiya|heya|good morning|good afternoon|good evening|what's up|whats up)\b/)) {
    return pick(RESPONSES.greeting);
  }

  if (msg.match(/\b(chore|task|clean|wash|vacuum|sweep|mop|cook|help|dish|laundry|homework|tidy|wipe|scrub|dust|organize|pick up)\b/)) {
    if (tasks.length > 0) {
      const pending = tasks.filter(t => t.status !== 'completed');
      if (pending.length > 0) {
        return `You have ${pending.length} task${pending.length > 1 ? 's' : ''} waiting! ${pick(RESPONSES.chores)}`;
      }
    }
    return pick(RESPONSES.chores);
  }

  if (msg.match(/\b(reward|earn|earning|allowance|goal|save|saving|prize|points|redeem|cash out)\b/)) {
    if (completedTasks.length > 0) {
      return `You've completed ${completedTasks.length} task${completedTasks.length > 1 ? 's' : ''} — great work! ${pick(RESPONSES.rewards)}`;
    }
    return pick(RESPONSES.rewards);
  }

  if (msg.match(/\b(bored|boring|nothing to do|so bored|super bored)\b/)) return pick(RESPONSES.boredom);

  if (msg.match(/\b(hard|difficult|too much|cant|can't|give up|too hard|impossible|struggle|struggling|overwhelm)\b/)) return pick(RESPONSES.difficulty);

  if (msg.match(/\b(frustrated|overwhelmed|upset|sad|angry|mad|unhappy|stressed|anxious|nervous|scared|worried)\b/)) return pick(RESPONSES.feelings);

  if (msg.match(/\b(finished|done|completed|i did it|all done|check|crushed it|nailed it)\b/)) return pick(RESPONSES.accomplishment);

  if (msg.match(/\b(money|allowance|buy|dollar|dollars|cents|spend|spending|save|saving|piggy bank|purchase)\b/)) return pick(RESPONSES.money);

  if (msg.match(/\b(school|homework|study|studying|class|teacher|test|quiz|grade|reading|math|science|history|learning)\b/)) return pick(RESPONSES.school);

  if (msg.match(/\b(motivat|motivation|inspire|encouragement|tired|exhausted|push me|cheer)\b/)) return pick(RESPONSES.motivation);

  if (msg.match(/\b(help|what can you do|who are you|what are you|chip are you|about you)\b/)) return pick(RESPONSES.help);

  // ── New Categories (10) ───────────────────────────────────────────────────

  // friends — playing, sharing, conflict, best friend, hanging out
  if (msg.match(/\b(friend|friends|best friend|bestie|buddy|pal|hang out|hanging out|play date|playdate|fight with|argument|sharing|share with|teammate|classmate|new friend|make friends)\b/)) {
    return pick(RESPONSES.friends);
  }

  // family — siblings, parents, guardians, relatives, family time
  if (msg.match(/\b(family|families|brother|sister|sibling|mom|dad|parent|parents|grandma|grandpa|grandparent|aunt|uncle|cousin|guardian|home life|family time|family night)\b/)) {
    return pick(RESPONSES.family);
  }

  // pets — animals, caring for pets, pet chores
  if (msg.match(/\b(pet|pets|dog|dogs|cat|cats|puppy|kitten|fish|hamster|rabbit|bunny|bird|turtle|lizard|animal|animals|walk the dog|feed the|groom|paw|paws|fur|feathers|cage|aquarium|litter box)\b/)) {
    return pick(RESPONSES.pets);
  }

  // hobbies — drawing, building, reading, music, sports, crafts, coding
  if (msg.match(/\b(hobby|hobbies|draw|drawing|paint|painting|build|building|lego|craft|crafts|knit|sewing|reading|read a book|music|instrument|guitar|piano|drums|violin|sport|sports|dance|dancing|coding|coding|sketch|sketching|collect|collecting)\b/)) {
    return pick(RESPONSES.hobbies);
  }

  // games — video games, board games, outdoor games, apps
  if (msg.match(/\b(game|games|video game|board game|card game|play outside|tag|hide and seek|minecraft|roblox|fortnite|pokemon|mario|nintendo|playstation|xbox|switch|controller|level up|gaming|app game|outdoor game|playground)\b/)) {
    return pick(RESPONSES.games);
  }

  // tv_movies — shows, movies, streaming, characters, cartoons
  if (msg.match(/\b(tv|television|show|shows|movie|movies|cartoon|cartoons|stream|netflix|disney|youtube|episode|character|watch|watching|series|film|anime|favorite show|favorite movie)\b/)) {
    return pick(RESPONSES.tv_movies);
  }

  // weather_seasons — rain, snow, summer, winter, spring, fall, weather talk
  if (msg.match(/\b(weather|rain|raining|rainy|snow|snowing|snowy|sunny|sun|sunshine|hot outside|cold outside|wind|windy|storm|stormy|cloud|cloudy|spring|summer|fall|autumn|winter|season|seasons|outside today|go outside)\b/)) {
    return pick(RESPONSES.weather_seasons);
  }

  // weekend_plans — weekend, vacation, trip, plans, days off
  if (msg.match(/\b(weekend|weekends|saturday|sunday|vacation|vacations|trip|trips|day off|days off|spring break|summer break|holiday|holidays|plan|plans|going somewhere|staycation|outing|road trip|visiting|travel)\b/)) {
    return pick(RESPONSES.weekend_plans);
  }

  // food — eating, favorite foods, cooking, snacks, meals, hunger
  if (msg.match(/\b(food|foods|eat|eating|hungry|hunger|snack|snacks|meal|meals|dinner|lunch|breakfast|cook|cooking|bake|baking|recipe|recipes|pizza|tacos|sandwich|salad|dessert|cake|cookie|cookies|taste|delicious|yummy|favorite food)\b/)) {
    return pick(RESPONSES.food);
  }

  // dreams_goals — future, career, aspiration, what to be, ambition
  if (msg.match(/\b(dream|dreams|goal|goals|want to be|future|someday|aspiration|ambition|career|job|when i grow up|become|astronaut|doctor|athlete|artist|scientist|teacher|inventor|hero|famous|successful|achieve|achievement)\b/)) {
    return pick(RESPONSES.dreams_goals);
  }

  // Rotate through default phrases
  const phrase = RESPONSES.default[defaultIndex % RESPONSES.default.length];
  defaultIndex++;
  return phrase;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Component ─────────────────────────────────────────────────────────────────

function PreProgrammedBot({ childName, tasks = [], completedTasks = [] }) {
  const greeting = childName
    ? `Hey ${childName}! I'm Chip the Chore Champion! 🏆 Ready to have an awesome day?`
    : "Hey there! I'm Chip the Chore Champion! 🏆 Ready to have an awesome day?";

  const [messages, setMessages] = useState([
    { id: 1, from: 'chip', text: greeting }
  ]);
  const [input, setInput] = useState('');
  const [expanded, setExpanded] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (expanded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, expanded]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    const userMsg = { id: Date.now(), from: 'user', text: trimmed };
    const chipReply = {
      id: Date.now() + 1,
      from: 'chip',
      text: getResponse(trimmed, tasks, completedTasks)
    };

    setMessages(prev => [...prev, userMsg, chipReply]);
    setInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick-reply suggestions
  const suggestions = [
    '💪 Motivate me!',
    '📋 Chore tips',
    '🏆 My rewards',
    '👋 Hi Chip!'
  ];

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'warning.main',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(45deg, #FF9800, #FFB74D)',
          color: 'white',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(e => !e)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: 36, height: 36, fontSize: 20 }}>
            🏆
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
              Chip the Chore Champion!
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Your personal cheer squad 🌟
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="Kid Safe"
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontSize: '0.7rem' }}
          />
          <IconButton sx={{ color: 'white', minWidth: 48, minHeight: 48 }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* Messages */}
        <Box
          sx={{
            height: 280,
            overflowY: 'auto',
            bgcolor: '#fffde7',
            px: 1,
            py: 1
          }}
        >
          {messages.map((msg) => (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start',
                mb: 1,
                alignItems: 'flex-end',
                gap: 1
              }}
            >
              {msg.from === 'chip' && (
                <Avatar sx={{ bgcolor: 'warning.main', width: 28, height: 28, fontSize: 16 }}>
                  🏆
                </Avatar>
              )}
              <Paper
                elevation={1}
                sx={{
                  px: 1.5,
                  py: 1,
                  maxWidth: '75%',
                  borderRadius: msg.from === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  bgcolor: msg.from === 'user' ? 'warning.light' : 'white',
                  color: msg.from === 'user' ? 'warning.contrastText' : 'text.primary'
                }}
              >
                <Typography variant="body2">{msg.text}</Typography>
              </Paper>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        {/* Quick replies */}
        <Box sx={{ px: 2, py: 1, bgcolor: '#fff8e1', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {suggestions.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              onClick={() => {
                setInput(s.replace(/^[^\w]+/, '').trim());
                setTimeout(() => {
                  const userMsg = { id: Date.now(), from: 'user', text: s };
                  const chipReply = { id: Date.now() + 1, from: 'chip', text: getResponse(s, tasks, completedTasks) };
                  setMessages(prev => [...prev, userMsg, chipReply]);
                }, 0);
              }}
              sx={{
                cursor: 'pointer',
                bgcolor: 'warning.light',
                '&:hover': { bgcolor: 'warning.main', color: 'white' }
              }}
            />
          ))}
        </Box>

        <Divider />

        {/* Input */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, gap: 1, bgcolor: 'white' }}>
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Say something to Chip..."
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: 200 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#fffde7'
              }
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim()}
            sx={{
              bgcolor: 'warning.main',
              color: 'white',
              '&:hover': { bgcolor: 'warning.dark' },
              '&:disabled': { bgcolor: 'grey.300' }
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Collapse>
    </Card>
  );
}

export default PreProgrammedBot;